import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, Outlet, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  Pagination,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ReplayIcon from "@mui/icons-material/Replay";
import api, { API_BASE_URL } from "../../src/utils/api";

const ViolationsPage = () => {
  const [violations, setViolations] = useState([]);
  const [filteredViolations, setFilteredViolations] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusCounts, setStatusCounts] = useState({ open: 0, closed: 0, regression: 0 });
  
  const [viewMode, setViewMode] = useState("cards"); // "cards" or "table"
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const { id } = useParams();

  useEffect(() => {
    if (!id) {
      const fetchViolations = async () => {
        try {
          const response = await api.get(`/getViolations`);
          setViolations(response.data);
          setFilteredViolations(response.data);
          calculateStatusCounts(response.data);
        } catch (error) {
          console.error("Failed to fetch violations:", error);
        }
      };

      fetchViolations();
    }
  }, [id]);

  useEffect(() => {
    const fetchViolationTypes = async () => {
      try {
        const response = await api.get(
          `/getCcrViolationTypes`
        );
        setViolationTypes(response.data);
      } catch (error) {
        console.error("Failed to fetch violation types:", error);
      }
    };

    fetchViolationTypes();
  }, []);

  const calculateStatusCounts = (violations) => {
    const counts = violations.reduce((acc, violation) => {
      const status = violation.status.toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    setStatusCounts(counts);
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  
    const filtered = violations.filter((violation) => {
      const violationType = violationTypes.find(
        (type) => type.id === violation.ccr_violation_type_id
      );
      const violationId = `vlt-${violation.id}`; // Include "VLT-" prefix for searching
  
      return (
        violationId.includes(query) ||
        (violationType && violationType.title.toLowerCase().includes(query)) ||
        violation.status.toLowerCase().includes(query) ||
        (violation.property_id && violation.property_id.toString().includes(query)) // Search by Property ID
      );
    });
  
    setFilteredViolations(filtered);
    setCurrentPage(1); // Reset to the first page when searching
  };
  



  const paginatedViolations = filteredViolations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  const [filterStatus, setFilterStatus] = useState(""); // Track active filter

const handleStatusFilter = (status) => {
  if (filterStatus === status) {
    // If already selected, reset to show all
    setFilterStatus("");
    setFilteredViolations(violations);
  } else {
    // Apply filter based on status
    setFilterStatus(status);
    const filtered = violations.filter(v => v.status.toLowerCase() === status);
    setFilteredViolations(filtered);
  }
};


const handlePageChange = (event, value) => {
  setCurrentPage(value);
};

  if (id) return <Outlet />;
  const columns = [
    {
      field: "id",
      headerName: "Violation ID",
      width: 120,
      valueGetter: (params) => (params.row?.id ? `VLT-${params.row.id}` : "N/A"),
    },
    { field: "property_id", headerName: "Property ID", width: 130 },
    {
      field: "ccr_violation_type_id",
      headerName: "Violation Type",
      width: 200,
      valueGetter: (params) => {
        const violationType = violationTypes.find((type) => type.id === params.row?.ccr_violation_type_id);
        return violationType ? violationType.title : "N/A";
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) =>
        params.row?.status ? (
          <Chip
            label={params.row.status.toUpperCase()}
            color={
              params.row.status.toLowerCase() === "open"
                ? "warning"
                : params.row.status.toLowerCase() === "closed"
                ? "success"
                : "error"
            }
          />
        ) : (
          "N/A"
        ),
    },
    {
      field: "violated_at",
      headerName: "Violation Date",
      width: 150,
      valueGetter: (params) => (params.row?.violated_at ? new Date(params.row.violated_at).toLocaleDateString() : "N/A"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) =>
        params.row?.id ? (
          <Button component={Link} to={`${params.row.id}`} variant="outlined" size="small">
            View Details
          </Button>
        ) : (
          "N/A"
        ),
    },
  ];
  




  return (
    <Box sx={{ padding: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>Violations</Typography>
        <Button
          variant="outlined"
          startIcon={viewMode === "cards" ? <TableChartIcon /> : <ViewModuleIcon />}
          onClick={() => setViewMode(viewMode === "cards" ? "table" : "cards")}
        >
          {viewMode === "cards" ? "Table View" : "Card View"}
        </Button>
      </Box>
  
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField fullWidth placeholder="Search by ID, Property, Type, or Status" value={searchQuery} onChange={handleSearch} variant="outlined" />
      </Box>
  
      {/* Status Summary */}
      <Box sx={{ display: "flex", justifyContent: "space-around", marginBottom: 3 }}>
        <Card
          sx={{ width: "30%", textAlign: "center", backgroundColor: "#fffde7", padding: 2, borderRadius: 2, cursor: "pointer", border: filterStatus === "open" ? "3px solid #ffc107" : "none" }}
          onClick={() => handleStatusFilter("open")}
        >
          <CardContent>
            <ErrorOutlineIcon sx={{ color: "#ffc107", fontSize: 40 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 1 }}>Open</Typography>
            <Typography variant="h4">{statusCounts.open || 0}</Typography>
          </CardContent>
        </Card>
  
        <Card
          sx={{ width: "30%", textAlign: "center", backgroundColor: "#e8f5e9", padding: 2, borderRadius: 2, cursor: "pointer", border: filterStatus === "closed" ? "3px solid #4caf50" : "none" }}
          onClick={() => handleStatusFilter("closed")}
        >
          <CardContent>
            <CheckCircleOutlineIcon sx={{ color: "#4caf50", fontSize: 40 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 1 }}>Closed</Typography>
            <Typography variant="h4">{statusCounts.closed || 0}</Typography>
          </CardContent>
        </Card>
  
        <Card
          sx={{ width: "30%", textAlign: "center", backgroundColor: "#ffebee", padding: 2, borderRadius: 2, cursor: "pointer", border: filterStatus === "regression" ? "3px solid #f44336" : "none" }}
          onClick={() => handleStatusFilter("regression")}
        >
          <CardContent>
            <ReplayIcon sx={{ color: "#f44336", fontSize: 40 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 1 }}>Regression</Typography>
            <Typography variant="h4">{statusCounts.regression || 0}</Typography>
          </CardContent>
        </Card>
      </Box>
  
      {/* Violations List (Conditional Rendering for Table View & Card View) */}
      {viewMode === "table" ? (
        <Box sx={{ height: 500, width: "100%", mt: 2 }}>
          <DataGrid
            rows={filteredViolations}
            columns={columns}
            getRowId={(row) => row.id || Math.random()}
            pageSize={10}
            disableSelectionOnClick
          />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {paginatedViolations.length > 0 ? (
            paginatedViolations.map((violation) => {
              const violationType = violationTypes.find((type) => type.id === violation.ccr_violation_type_id);
  
              return (
                <Grid item xs={12} sm={6} md={4} key={violation.id}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: 2,
                      borderLeft: `4px solid ${
                        violation.status.toLowerCase() === "open"
                          ? "#ffc107"
                          : violation.status.toLowerCase() === "closed"
                          ? "#4caf50"
                          : "#f44336"
                      }`
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        VLT-{violation.id}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                        Property ID: {violation.property_id || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
                        {violationType ? violationType.title : "Type Not Found"}
                      </Typography>
                      <Chip
                        label={violation.status.toUpperCase()}
                        sx={{
                          marginBottom: 2,
                          backgroundColor:
                            violation.status.toLowerCase() === "open"
                              ? "#ffc107"
                              : violation.status.toLowerCase() === "closed"
                              ? "#4caf50"
                              : "#f44336",
                          color: "#fff",
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(violation.violated_at).toLocaleDateString()}
                      </Typography>
                      <Button component={Link} to={`${violation.id}`} variant="outlined" size="small" sx={{ marginTop: 2 }}>
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Typography variant="body1" color="text.secondary">
              No violations found.
            </Typography>
          )}
        </Grid>
      )}
  
      {/* Pagination should be outside to apply to both views */}
      <Box sx={{ marginTop: 3, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(filteredViolations.length / itemsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
  
};

export default ViolationsPage;
